import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, CalendarPlus, ExternalLink, MapPin, RefreshCw, Users, Video } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardHeader, CardTitle, Input, Select, Switch, Textarea } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { AppointmentItem, AvailabilityItem, TechnicianItem } from '@/lib/types';
import { cn } from '@/lib/utils';

const onError = (e: unknown) => toast.error(errorMessage(e));

export const TECH_COLORS = ['#2563eb', '#16a34a', '#d97706', '#9333ea', '#dc2626', '#0891b2', '#db2777', '#4d7c0f', '#7c3aed', '#ea580c'];
export function techColor(id: string, all: { id: string }[]) {
  const idx = all.findIndex((t) => t.id === id);
  return TECH_COLORS[(idx >= 0 ? idx : 0) % TECH_COLORS.length]!;
}

export function useTechnicians() {
  return useQuery({ queryKey: ['appointments', 'technicians'], queryFn: () => api<{ items: TechnicianItem[] }>('/appointments/technicians'), staleTime: 60_000 });
}

const time = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' });
const day = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
export const formatTime = (d: string | Date) => time.format(new Date(d));
export const formatRange = (a: { startAt: string; endAt: string }) => {
  const s = new Date(a.startAt);
  const e = new Date(a.endAt);
  return s.toDateString() === e.toDateString() ? `${day.format(s)}, ${time.format(s)}–${time.format(e)} Uhr` : `${day.format(s)} ${time.format(s)} – ${day.format(e)} ${time.format(e)}`;
};

const pad = (n: number) => String(n).padStart(2, '0');
export const toDateInput = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toTimeInput = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const combine = (date: string, t: string) => new Date(`${date}T${t}:00`);

const REMINDERS = [
  { value: '', label: 'Keine Erinnerung' },
  { value: '60', label: '1 Stunde vorher' },
  { value: '180', label: '3 Stunden vorher' },
  { value: '1440', label: '1 Tag vorher' },
  { value: '2880', label: '2 Tage vorher' },
];

export interface AppointmentDefaults {
  title?: string;
  description?: string;
  location?: string;
  start?: Date;
  technicianIds?: string[];
  customerEmails?: string[];
  ticketId?: string;
  repairId?: string;
  customerId?: string;
}

/** Bezug eines Termins: Ticket oder Reparatur (setzt auch den Kunden). */
interface LinkRef {
  kind: 'ticket' | 'repair';
  id: string;
  label: string;
  customerId: string | null;
}

/** Suche nach Ticket oder Reparatur für Termine aus dem Kalender. */
function LinkPicker({ value, onChange }: { value: LinkRef | null; onChange: (v: LinkRef | null) => void }) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const enc = encodeURIComponent(debounced);
  const { data: tickets } = useQuery({
    queryKey: ['appointments', 'link', 'tickets', debounced],
    queryFn: () => api<{ items: { id: string; number: string; subject: string; customer: { id: string; name: string } | null }[] }>(`/tickets?q=${enc}&pageSize=6`),
    enabled: debounced.length >= 2,
    retry: false,
  });
  const { data: repairs } = useQuery({
    queryKey: ['appointments', 'link', 'repairs', debounced],
    queryFn: () => api<{ items: { id: string; number: string; customer: { id: string; name: string }; device: { label: string } }[] }>(`/repairs?q=${enc}&state=all&pageSize=6`),
    enabled: debounced.length >= 2,
    retry: false,
  });
  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
        <span className="truncate">
          <span className="text-muted-foreground">{value.kind === 'ticket' ? 'Ticket' : 'Reparatur'}:</span> {value.label}
        </span>
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
          Entfernen
        </Button>
      </div>
    );
  }
  const results: LinkRef[] = [
    ...(tickets?.items ?? []).map((t) => ({ kind: 'ticket' as const, id: t.id, label: `${t.number} – ${t.subject}${t.customer ? ` (${t.customer.name})` : ''}`, customerId: t.customer?.id ?? null })),
    ...(repairs?.items ?? []).map((r) => ({ kind: 'repair' as const, id: r.id, label: `${r.number} – ${r.device.label} (${r.customer.name})`, customerId: r.customer.id })),
  ];
  return (
    <div className="grid gap-1">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ticket- oder Reparaturnummer, Betreff, Kunde …" aria-label="Ticket oder Reparatur suchen" />
      {debounced.length >= 2 && (
        <ul className="max-h-48 overflow-y-auto rounded-md border text-sm" role="listbox" aria-label="Treffer">
          {results.length === 0 && <li className="px-3 py-2 text-muted-foreground">Keine Treffer</li>}
          {results.map((r) => (
            <li key={`${r.kind}-${r.id}`}>
              <button type="button" role="option" aria-selected={false} className="w-full truncate px-3 py-1.5 text-left hover:bg-muted" onClick={() => onChange(r)}>
                <span className="mr-1 text-xs text-muted-foreground">{r.kind === 'ticket' ? 'Ticket' : 'Reparatur'}</span> {r.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface FormState {
  link: LinkRef | null;
  title: string;
  description: string;
  location: string;
  date: string;
  startTime: string;
  endTime: string;
  technicianIds: string[];
  customerEmails: string;
  isOnlineMeeting: boolean;
  reminderMinutes: string;
}

function initialForm(edit: AppointmentItem | null, defaults: AppointmentDefaults, me: string): FormState {
  if (edit) {
    const s = new Date(edit.startAt);
    return {
      link: edit.ticket
        ? { kind: 'ticket', id: edit.ticket.id, label: `${edit.ticket.number} – ${edit.ticket.subject}`, customerId: edit.customer?.id ?? null }
        : edit.repair
          ? { kind: 'repair', id: edit.repair.id, label: edit.repair.number, customerId: edit.customer?.id ?? null }
          : null,
      title: edit.title,
      description: edit.description ?? '',
      location: edit.location ?? '',
      date: toDateInput(s),
      startTime: toTimeInput(s),
      endTime: toTimeInput(new Date(edit.endAt)),
      technicianIds: edit.technicians.map((t) => t.id),
      customerEmails: edit.customerEmails.join(', '),
      isOnlineMeeting: edit.isOnlineMeeting,
      reminderMinutes: edit.reminderMinutes ? String(edit.reminderMinutes) : '',
    };
  }
  const start = defaults.start ?? (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d;
  })();
  return {
    link: null,
    title: defaults.title ?? '',
    description: defaults.description ?? '',
    location: defaults.location ?? '',
    date: toDateInput(start),
    startTime: toTimeInput(start),
    endTime: toTimeInput(new Date(start.getTime() + 60 * 60_000)),
    technicianIds: defaults.technicianIds ?? [me],
    customerEmails: (defaults.customerEmails ?? []).join(', '),
    isOnlineMeeting: false,
    reminderMinutes: defaults.customerEmails?.length ? '1440' : '',
  };
}

/** Termin anlegen oder bearbeiten – mit Verfügbarkeitsprüfung (Portal + Outlook). */
export function AppointmentDialog({
  open,
  onClose,
  edit,
  defaults = {},
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  edit?: AppointmentItem | null;
  defaults?: AppointmentDefaults;
  onSaved?: (a: AppointmentItem) => void;
}) {
  const qc = useQueryClient();
  const { me } = useAuth();
  const { data: techs } = useTechnicians();
  const [form, setForm] = useState<FormState>(() => initialForm(edit ?? null, defaults, me?.user.id ?? ''));
  useEffect(() => {
    if (open) setForm(initialForm(edit ?? null, defaults, me?.user.id ?? ''));
  }, [open, edit]);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const start = form.date && form.startTime ? combine(form.date, form.startTime) : null;
  const end = form.date && form.endTime ? combine(form.date, form.endTime) : null;
  const validTime = start && end && end > start;
  const emails = form.customerEmails
    .split(/[,;\s]+/)
    .map((e) => e.trim())
    .filter(Boolean);

  const dayStart = form.date ? combine(form.date, '00:00') : null;
  const availabilityKey = ['appointments', 'availability', form.technicianIds.join(','), form.date, edit?.id];
  const { data: availability, isFetching: checking } = useQuery({
    queryKey: availabilityKey,
    queryFn: () =>
      api<{ items: AvailabilityItem[] }>('/appointments/availability', {
        method: 'POST',
        body: { technicianIds: form.technicianIds, from: dayStart!.toISOString(), to: new Date(dayStart!.getTime() + 86_400_000).toISOString(), excludeAppointmentId: edit?.id },
      }),
    enabled: open && form.technicianIds.length > 0 && dayStart !== null,
  });
  const conflicts = useMemo(() => {
    if (!availability || !validTime) return [];
    return availability.items.flatMap((t) =>
      t.busy.filter((b) => new Date(b.start) < end! && new Date(b.end) > start!).map((b) => ({ name: t.name, ...b })),
    );
  }, [availability, validTime, start, end]);

  // Aus Ticket bzw. Reparatur heraus ist der Bezug fest; im Kalender frei wählbar.
  const fixedLink = !edit && Boolean(defaults.ticketId || defaults.repairId);
  const save = useMutation({
    mutationFn: () => {
      const body = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        location: form.location.trim() || null,
        startAt: start!.toISOString(),
        endAt: end!.toISOString(),
        technicianIds: form.technicianIds,
        customerEmails: emails,
        isOnlineMeeting: form.isOnlineMeeting,
        reminderMinutes: form.reminderMinutes ? Number(form.reminderMinutes) : null,
        ticketId: fixedLink ? defaults.ticketId ?? null : form.link?.kind === 'ticket' ? form.link.id : null,
        repairId: fixedLink ? defaults.repairId ?? null : form.link?.kind === 'repair' ? form.link.id : null,
        customerId: fixedLink ? defaults.customerId ?? null : form.link?.customerId ?? (edit ? edit.customer?.id ?? null : defaults.customerId ?? null),
      };
      return edit ? api<AppointmentItem>(`/appointments/${edit.id}`, { method: 'PUT', body }) : api<AppointmentItem>('/appointments', { method: 'POST', body });
    },
    onSuccess: (a) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['repairs'] });
      if (a.syncError) toast.warning(`Termin gespeichert, aber nicht in Outlook übertragen: ${a.syncError}`, { duration: 12_000 });
      else toast.success(a.outlookSynced ? 'Termin gespeichert und in Outlook eingetragen' : 'Termin gespeichert');
      onSaved?.(a);
      onClose();
    },
    onError,
  });

  const toggleTech = (id: string) =>
    set('technicianIds', form.technicianIds.includes(id) ? form.technicianIds.filter((t) => t !== id) : [...form.technicianIds, id]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent wide>
        <form
          className="flex min-h-0 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{edit ? 'Termin bearbeiten' : 'Termin planen'}</DialogTitle>
            <DialogDescription>
              Der erste ausgewählte Mitarbeiter ist Organisator in Outlook; weitere Mitarbeiter und Kunden werden eingeladen.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            {!fixedLink && (
              <Field label="Bezug (optional)" hint="Termin einem Ticket oder einer Reparatur zuordnen – erscheint dort im Verlauf.">
                <LinkPicker
                  value={form.link}
                  onChange={(link) =>
                    setForm((f) => ({ ...f, link, title: link && !f.title.trim() ? (link.kind === 'ticket' ? `Termin ${link.label}` : `Reparatur ${link.label}`).slice(0, 200) : f.title }))
                  }
                />
              </Field>
            )}
            <Field label="Titel" htmlFor="ap-title">
              <Input id="ap-title" required value={form.title} onChange={(e) => set('title', e.target.value)} />
            </Field>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Datum" htmlFor="ap-date">
                <Input id="ap-date" type="date" required value={form.date} onChange={(e) => set('date', e.target.value)} />
              </Field>
              <Field label="Beginn" htmlFor="ap-start">
                <Input id="ap-start" type="time" step={900} required value={form.startTime} onChange={(e) => set('startTime', e.target.value)} />
              </Field>
              <Field label="Ende" htmlFor="ap-end" error={start && end && !validTime ? 'Ende vor Beginn' : undefined}>
                <Input id="ap-end" type="time" step={900} required value={form.endTime} onChange={(e) => set('endTime', e.target.value)} />
              </Field>
            </div>

            <fieldset>
              <legend className="mb-2 text-sm font-medium">Mitarbeitende</legend>
              <div className="flex flex-wrap gap-1.5">
                {techs?.items.map((t) => {
                  const active = form.technicianIds.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => toggleTech(t.id)}
                      aria-pressed={active}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors',
                        active ? 'border-primary bg-primary/10 text-primary' : 'hover:bg-muted',
                      )}
                    >
                      <span className="size-2 rounded-full" style={{ background: techColor(t.id, techs.items) }} />
                      {t.name}
                      {active && form.technicianIds[0] === t.id && form.technicianIds.length > 1 && <span className="text-xs">(Organisator)</span>}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {form.technicianIds.length > 0 && availability && (
              <div className="rounded-md border p-3 text-sm" aria-live="polite">
                <p className="mb-2 flex items-center gap-2 font-medium">
                  <CalendarClock className="size-4" /> Belegung am {form.date && day.format(combine(form.date, '12:00'))}
                  {checking && <RefreshCw className="size-3.5 animate-spin text-muted-foreground" />}
                </p>
                <ul className="grid gap-1">
                  {availability.items.map((t) => (
                    <li key={t.technicianId} className="flex flex-wrap gap-x-2">
                      <span className="font-medium">{t.name}:</span>
                      {t.busy.length === 0 ? (
                        <span className="text-muted-foreground">frei</span>
                      ) : (
                        t.busy
                          .sort((a, b) => a.start.localeCompare(b.start))
                          .map((b, i) => (
                            <span key={i} className="text-muted-foreground">
                              {formatTime(b.start)}–{formatTime(b.end)} {b.label}
                              {b.source === 'outlook' && ' (Outlook)'}
                            </span>
                          ))
                      )}
                      {t.outlookError && <span className="text-destructive">Outlook: {t.outlookError}</span>}
                    </li>
                  ))}
                </ul>
                {conflicts.length > 0 && (
                  <p className="mt-2 flex items-start gap-2 text-amber-700 dark:text-amber-300">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                    Überschneidung: {conflicts.map((c) => `${c.name} (${formatTime(c.start)}–${formatTime(c.end)})`).join(', ')}
                  </p>
                )}
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Ort" htmlFor="ap-loc">
                <Input id="ap-loc" value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="z. B. beim Kunden, Werkstatt" />
              </Field>
              <Field label="Kunden einladen (E-Mail)" htmlFor="ap-cust" hint="Mehrere Adressen mit Komma trennen.">
                <Input id="ap-cust" value={form.customerEmails} onChange={(e) => set('customerEmails', e.target.value)} />
              </Field>
              <Field label="Erinnerung an Kunden" htmlFor="ap-rem">
                <Select id="ap-rem" value={form.reminderMinutes} onChange={(e) => set('reminderMinutes', e.target.value)}>
                  {REMINDERS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <label className="flex items-center gap-3 self-end rounded-md border p-2.5 text-sm">
                <Switch checked={form.isOnlineMeeting} onCheckedChange={(v) => set('isOnlineMeeting', v)} />
                Teams-Besprechung erstellen
              </label>
            </div>
            <Field label="Beschreibung" htmlFor="ap-desc">
              <Textarea id="ap-desc" rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" loading={save.isPending} disabled={!form.title.trim() || !validTime || form.technicianIds.length === 0}>
              {conflicts.length > 0 ? 'Trotz Überschneidung speichern' : 'Speichern'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Termindetails mit Bearbeiten, Absagen und erneutem Outlook-Abgleich. */
export function AppointmentDetailDialog({ appointment, onClose }: { appointment: AppointmentItem | null; onClose: () => void }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [editing, setEditing] = useState(false);
  const [confirmDialog, confirm] = useConfirm();
  const a = appointment;
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['appointments'] });
    qc.invalidateQueries({ queryKey: ['tickets'] });
    qc.invalidateQueries({ queryKey: ['repairs'] });
  };
  const cancel = useMutation({
    mutationFn: () => api<AppointmentItem>(`/appointments/${a!.id}/cancel`, { method: 'POST', body: {} }),
    onSuccess: () => {
      toast.success('Termin abgesagt');
      refresh();
      onClose();
    },
    onError,
  });
  const sync = useMutation({
    mutationFn: () => api<AppointmentItem>(`/appointments/${a!.id}/sync`, { method: 'POST' }),
    onSuccess: (r) => {
      if (r.syncError) toast.error(r.syncError);
      else toast.success('In Outlook aktualisiert');
      refresh();
      onClose();
    },
    onError,
  });

  if (editing && a) return <AppointmentDialog open edit={a} onClose={() => (setEditing(false), onClose())} />;

  return (
    <Dialog open={a !== null} onOpenChange={(o) => !o && onClose()}>
      {confirmDialog}
      <DialogContent>
        {a && (
          <>
            <DialogHeader>
              <DialogTitle className="pr-8">
                {a.title} {a.status === 'CANCELLED' && <Badge variant="destructive">abgesagt</Badge>}
              </DialogTitle>
              <DialogDescription>{formatRange(a)}</DialogDescription>
            </DialogHeader>
            <DialogBody className="grid gap-3 text-sm">
              <p className="flex items-start gap-2">
                <Users className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                {a.technicians.map((t) => t.name).join(', ')}
                {a.customerEmails.length > 0 && <span className="text-muted-foreground"> · Kunde: {a.customerEmails.join(', ')}</span>}
              </p>
              {a.location && (
                <p className="flex items-start gap-2">
                  <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" /> {a.location}
                </p>
              )}
              {a.onlineMeetingUrl && (
                <p className="flex items-start gap-2">
                  <Video className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  <a href={a.onlineMeetingUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                    Teams-Besprechung beitreten <ExternalLink className="inline size-3" />
                  </a>
                </p>
              )}
              {(a.ticket || a.repair || a.customer) && (
                <p className="flex flex-wrap gap-x-3">
                  {a.ticket && (
                    <Link to={`/tickets/${a.ticket.id}`} className="text-primary hover:underline" onClick={onClose}>
                      Ticket {a.ticket.number}
                    </Link>
                  )}
                  {a.repair && (
                    <Link to={`/repairs/${a.repair.id}`} className="text-primary hover:underline" onClick={onClose}>
                      Reparatur {a.repair.number}
                    </Link>
                  )}
                  {a.customer && (
                    <Link to={`/customers/${a.customer.id}`} className="text-primary hover:underline" onClick={onClose}>
                      {a.customer.name}
                    </Link>
                  )}
                </p>
              )}
              {a.description && <p className="whitespace-pre-wrap rounded-md bg-muted/50 p-3">{a.description}</p>}
              <p className="text-xs text-muted-foreground">{a.outlookSynced ? 'In Outlook eingetragen' : 'Nur im Serviceportal'}</p>
              {a.syncError && <Alert variant="destructive">Outlook: {a.syncError}</Alert>}
            </DialogBody>
            {can('calendar.manage') && a.status === 'CONFIRMED' && (
              <DialogFooter className="flex-wrap">
                {(a.syncError || !a.outlookSynced) && (
                  <Button variant="outline" onClick={() => sync.mutate()} loading={sync.isPending}>
                    <RefreshCw /> Mit Outlook abgleichen
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={async () =>
                    (await confirm({ title: 'Termin absagen?', description: 'Eingeladene Mitarbeitende und Kunden werden benachrichtigt.', confirmLabel: 'Absagen', destructive: true })) && cancel.mutate()
                  }
                  loading={cancel.isPending}
                >
                  Absagen
                </Button>
                <Button onClick={() => setEditing(true)}>Bearbeiten</Button>
              </DialogFooter>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Termine zu einem Ticket oder einer Reparatur. */
export function AppointmentsCard({ ticketId, repairId, defaults }: { ticketId?: string; repairId?: string; defaults: AppointmentDefaults }) {
  const { can } = useAuth();
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<AppointmentItem | null>(null);
  const query = ticketId ? `ticketId=${ticketId}` : `repairId=${repairId}`;
  const { data } = useQuery({
    queryKey: ['appointments', 'for', query],
    queryFn: () => api<{ items: AppointmentItem[] }>(`/appointments?${query}&includeCancelled=true`),
    enabled: can('calendar.read'),
  });
  if (!can('calendar.read')) return null;
  const items = data?.items ?? [];
  if (items.length === 0 && !can('calendar.manage')) return null;

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm">Termine</CardTitle>
        {can('calendar.manage') && (
          <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
            <CalendarPlus /> Termin planen
          </Button>
        )}
      </CardHeader>
      <CardContent className="grid gap-2">
        {items.length === 0 && <p className="text-sm text-muted-foreground">Keine Termine.</p>}
        {items.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => setOpen(a)}
            className={cn('rounded-md border p-2.5 text-left text-sm transition-colors hover:bg-muted/60', a.status === 'CANCELLED' && 'opacity-60')}
          >
            <span className={cn('block font-medium', a.status === 'CANCELLED' && 'line-through')}>{a.title}</span>
            <span className="block text-muted-foreground">{formatRange(a)}</span>
            <span className="block text-muted-foreground">
              {a.technicians.map((t) => t.name).join(', ')}
              {a.syncError && <span className="text-destructive"> · Outlook-Fehler</span>}
            </span>
          </button>
        ))}
      </CardContent>
      <AppointmentDialog open={creating} onClose={() => setCreating(false)} defaults={{ ...defaults, ticketId, repairId }} />
      <AppointmentDetailDialog appointment={open} onClose={() => setOpen(null)} />
    </Card>
  );
}
